import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-copper-600">Owner Access</p>
          <h1 className="mt-3 font-serif text-3xl text-charcoal-900">Portfolio Studio</h1>
          <p className="mt-2 text-sm text-charcoal-500">Sign in to manage categories, work and media.</p>
        </div>
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="bg-surface-white border border-charcoal-900/10 rounded-2xl p-8 flex flex-col gap-4 shadow-sm"
        >
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            aria-label="Email"
            className="w-full bg-parchment border border-charcoal-900/15 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-copper-600 focus:ring-1 focus:ring-copper-600"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            aria-label="Password"
            className="w-full bg-parchment border border-charcoal-900/15 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-copper-600 focus:ring-1 focus:ring-copper-600"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-charcoal-900 text-parchment text-sm font-medium px-6 py-3.5 hover:bg-copper-600 disabled:opacity-60 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <p className="mt-6 text-center">
          <a href="/" className="text-xs text-charcoal-500 hover:text-copper-600 transition-colors">
            ← Back to website
          </a>
        </p>
      </div>
    </div>
  );
}
